/**
 * Phase 2b — in-canvas text edit helpers (contentEditable overlay).
 * Slice A: Enter newline; Ctrl/Cmd+Enter commits.
 * Slice B: selection offsets ↔ runs; DOM sync for range formatting.
 * Slice C: IME composition buffering; plain-text paste; exit flush.
 */

import {
  coalesceAdjacentTextRuns,
  flattenRunsToText,
  insertPlainTextIntoRuns,
  normalizeEditorTextParams,
  normalizeTextRun,
  normalizeTextRuns,
} from './imageEditorText.js';

/**
 * @param {KeyboardEvent|React.KeyboardEvent} event
 * @returns {'commit'|'newline'|'none'}
 */
export function textEditKeyAction(event) {
  if (!event || event.key !== 'Enter') return 'none';
  // Ctrl+Enter (Win/Linux) or Cmd+Enter (Mac) → commit
  if (event.ctrlKey || event.metaKey) {
    if (event.altKey || event.shiftKey) return 'none';
    return 'commit';
  }
  // Plain Enter / Shift+Enter → newline (browser default)
  return 'newline';
}

/**
 * Plain text from a contentEditable surface (\n for line breaks).
 * @param {HTMLElement|null|undefined} el
 * @returns {string}
 */
export function plainTextFromEditSurface(el) {
  if (!el) return '';
  return String(el.innerText || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ');
}

/**
 * Write plain text into a contentEditable (preserves \\n with white-space:pre-wrap).
 * @param {HTMLElement|null|undefined} el
 * @param {string} text
 */
export function setEditSurfacePlainText(el, text) {
  if (!el) return;
  const next = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (plainTextFromEditSurface(el) === next) return;
  el.textContent = next;
}

/**
 * Render runs[] into the edit surface as styled spans (Slice B).
 * @param {HTMLElement|null|undefined} el
 * @param {import('./imageEditorText').EditorTextRun[]|null|undefined} runs
 * @param {Partial<import('./imageEditorText').EditorTextParams>|null|undefined} [defaults]
 */
export function setEditSurfaceFromRuns(el, runs, defaults) {
  if (!el) return;
  const base = normalizeEditorTextParams(defaults || {});
  const normalized = coalesceAdjacentTextRuns(
    normalizeTextRuns(runs, base),
    base,
  );
  while (el.firstChild) el.removeChild(el.firstChild);
  for (const run of normalized) {
    const span = document.createElement('span');
    span.setAttribute('data-text-run', 'true');
    span.style.fontWeight = run.bold ? '700' : '400';
    span.style.fontStyle = run.italic ? 'italic' : 'normal';
    span.style.color = run.color || base.color;
    span.textContent = run.text;
    el.appendChild(span);
  }
  if (!el.firstChild) {
    el.appendChild(document.createTextNode(''));
  }
}

/**
 * Parse contentEditable DOM back into runs[] (preserves per-span styles).
 * @param {HTMLElement|null|undefined} el
 * @param {Partial<import('./imageEditorText').EditorTextParams>|null|undefined} [defaults]
 * @returns {ReturnType<typeof normalizeTextRun>[]}
 */
export function runsFromEditSurface(el, defaults) {
  const base = normalizeEditorTextParams(defaults || {});
  if (!el) {
    return normalizeTextRuns([{ text: '', ...base }], base);
  }
  /** @type {import('./imageEditorText').EditorTextRun[]} */
  const raw = [];

  /**
   * @param {string} text
   * @param {Partial<import('./imageEditorText').EditorTextRun>} style
   */
  const push = (text, style) => {
    const next = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (!next && raw.length) return;
    raw.push(
      normalizeTextRun(
        {
          text: next,
          bold: style.bold,
          italic: style.italic,
          color: style.color,
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
        },
        base,
      ),
    );
  };

  /**
   * @param {Element} node
   * @returns {Partial<import('./imageEditorText').EditorTextRun>}
   */
  const styleFromElement = (node) => {
    const tag = node.tagName;
    const cs = /** @type {HTMLElement} */ (node).style;
    const weight = String(cs.fontWeight || '');
    const bold =
      tag === 'B' ||
      tag === 'STRONG' ||
      weight === 'bold' ||
      weight === '700' ||
      Number(weight) >= 600;
    const italic =
      tag === 'I' ||
      tag === 'EM' ||
      String(cs.fontStyle || '') === 'italic';
    return {
      bold,
      italic,
      color: cs.color || base.color,
      fontFamily: base.fontFamily,
      fontSize: base.fontSize,
    };
  };

  const children = Array.from(el.childNodes);
  if (!children.length) {
    return normalizeTextRuns([{ text: '', ...base }], base);
  }

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      push(child.textContent || '', {
        bold: base.bold,
        italic: base.italic,
        color: base.color,
      });
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const elChild = /** @type {HTMLElement} */ (child);
    if (elChild.tagName === 'BR') {
      push('\n', {
        bold: base.bold,
        italic: base.italic,
        color: base.color,
      });
      continue;
    }
    const style = styleFromElement(elChild);
    // Prefer innerText so <br> inside a span becomes \n.
    const text = String(elChild.innerText || elChild.textContent || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
    push(text, style);
  }

  const flat = plainTextFromEditSurface(el);
  if (!raw.length) {
    return normalizeTextRuns([{ text: flat, ...base }], base);
  }
  // If DOM parse lost characters (browser quirks), fall back to plain one-run.
  if (flattenRunsToText(raw) !== flat && flat.length > 0 && raw.length === 1) {
    raw[0].text = flat;
  }
  return coalesceAdjacentTextRuns(raw, base);
}

/**
 * Character offset of a (node, offset) pair within the edit surface.
 * @param {HTMLElement} root
 * @param {Node|null} targetNode
 * @param {number} targetOffset
 * @returns {number}
 */
export function charOffsetInEditSurface(root, targetNode, targetOffset) {
  if (!root || !targetNode) return 0;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let count = 0;
  let node = walker.nextNode();
  while (node) {
    if (node === targetNode) {
      return count + Math.max(0, Math.min(targetOffset, (node.textContent || '').length));
    }
    count += (node.textContent || '').length;
    node = walker.nextNode();
  }
  // BR elements: approximate via plain text length if node is the root.
  if (targetNode === root) {
    return Math.max(0, Math.min(targetOffset, plainTextFromEditSurface(root).length));
  }
  return count;
}

/**
 * Read the current Selection as character offsets inside the edit surface.
 * @param {HTMLElement|null|undefined} root
 * @returns {{ start: number, end: number, collapsed: boolean }|null}
 */
export function getEditSurfaceSelectionOffsets(root) {
  if (!root || typeof window === 'undefined' || !window.getSelection) {
    return null;
  }
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const anchor = sel.anchorNode;
  const focus = sel.focusNode;
  if (!anchor || !focus) return null;
  if (!root.contains(anchor) || !root.contains(focus)) return null;
  const a = charOffsetInEditSurface(root, anchor, sel.anchorOffset);
  const b = charOffsetInEditSurface(root, focus, sel.focusOffset);
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  return { start, end, collapsed: start === end };
}

/**
 * Restore a character-offset selection inside the edit surface.
 * @param {HTMLElement|null|undefined} root
 * @param {number} startOffset
 * @param {number} endOffset
 */
export function setEditSurfaceSelectionOffsets(root, startOffset, endOffset) {
  if (!root || typeof window === 'undefined' || !window.getSelection) return;
  const sel = window.getSelection();
  if (!sel) return;
  const flatLen = plainTextFromEditSurface(root).length;
  let start = Math.max(0, Math.min(Number(startOffset) || 0, flatLen));
  let end = Math.max(0, Math.min(Number(endOffset) || 0, flatLen));
  if (start > end) {
    const t = start;
    start = end;
    end = t;
  }

  /**
   * @param {number} goal
   * @returns {{ node: Text, offset: number }|null}
   */
  const pointAt = (goal) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let count = 0;
    let node = walker.nextNode();
    let last = null;
    while (node) {
      last = node;
      const len = (node.textContent || '').length;
      if (count + len >= goal) {
        return { node: /** @type {Text} */ (node), offset: goal - count };
      }
      count += len;
      node = walker.nextNode();
    }
    if (last) {
      return {
        node: /** @type {Text} */ (last),
        offset: (last.textContent || '').length,
      };
    }
    return null;
  };

  const a = pointAt(start);
  const b = pointAt(end);
  if (!a || !b) return;
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Place caret at end of edit surface.
 * @param {HTMLElement|null|undefined} el
 */
export function focusEditSurfaceCaretEnd(el) {
  if (!el || typeof el.focus !== 'function') return;
  el.focus();
  const len = plainTextFromEditSurface(el).length;
  setEditSurfaceSelectionOffsets(el, len, len);
}

/**
 * True when the event target is the in-canvas text edit surface.
 * @param {EventTarget|null|undefined} target
 * @returns {boolean}
 */
export function isTextFloatEditSurfaceTarget(target) {
  if (!target || typeof target !== 'object') return false;
  if (typeof target.closest === 'function') {
    return Boolean(target.closest('[data-text-edit-surface="true"]'));
  }
  return target.getAttribute?.('data-text-edit-surface') === 'true';
}

/**
 * True when target is the range mini-toolbar (must not collapse selection).
 * @param {EventTarget|null|undefined} target
 * @returns {boolean}
 */
export function isTextRangeFormatToolbarTarget(target) {
  if (!target || typeof target !== 'object') return false;
  if (typeof target.closest === 'function') {
    return Boolean(target.closest('[data-text-range-toolbar="true"]'));
  }
  return target.getAttribute?.('data-text-range-toolbar') === 'true';
}

/**
 * Prefer clipboard text/plain for paste (strip rich source formatting).
 * @param {ClipboardEvent|React.ClipboardEvent|null|undefined} event
 * @returns {string}
 */
export function clipboardPlainTextFromPasteEvent(event) {
  const data = event?.clipboardData;
  if (!data || typeof data.getData !== 'function') return '';
  try {
    return String(data.getData('text/plain') || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\u00a0/g, ' ');
  } catch {
    return '';
  }
}

/**
 * Apply a plain-text paste into runs at the current edit-surface selection.
 * @param {HTMLElement|null|undefined} el
 * @param {string} plainText
 * @param {import('./imageEditorText').EditorTextRun[]|null|undefined} runs
 * @param {Partial<import('./imageEditorText').EditorTextParams>|null|undefined} [defaults]
 * @returns {{ runs: ReturnType<typeof normalizeTextRun>[], caret: number }}
 */
export function applyPlainTextPasteToEditSurface(el, plainText, runs, defaults) {
  const base = normalizeEditorTextParams(defaults || {});
  const offsets = getEditSurfaceSelectionOffsets(el);
  const flat = flattenRunsToText(normalizeTextRuns(runs, base));
  const start = offsets ? offsets.start : flat.length;
  const end = offsets ? offsets.end : flat.length;
  const nextRuns = insertPlainTextIntoRuns(runs, start, end, plainText, base);
  const caret = start + String(plainText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').length;
  return { runs: nextRuns, caret };
}

